import React, { useEffect, useState, useCallback } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import IconAdd from '@dealroadshow/uikit/core/components/Icon/IconAdd';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import { v4 as uuid } from 'uuid';
import cn from 'classnames';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import SortablePortfolio from './SortablePortfolio';
import { useFormState } from '@/Framework/UI/Organisms/FinalForm';
import {
  IFieldData,
  TSetFieldsData,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/ManagePortfoliosModal/ManagePortfoliosModal';
import { MAX_NUMBER_OF_PORTFOLIOS } from '@/finsight/ui/components/portfolioMonitor/constants';
import { useManagePortfoliosContext } from '@/finsight/application/PortfolioMonitor/ManagePortfoliosContext';

import styles from './managePortfoliosModal.scss';
import namePortfolioFormStyles from
    '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/NamePortfolioForm/namePortfolioForm.scss';

const tooltipContent = 'You have reached the maximum number \n' +
  'of Portfolios that can be created.';

interface IProps {
  onCancel: () => void,
  fieldsData: IFieldData[],
  setFieldsData: TSetFieldsData,
  dirtySinceLastSubmit: boolean,
  hasSubmitErrors: boolean,
}

const ManagePortfoliosForm = ({
  onCancel,
  fieldsData,
  setFieldsData,
  dirtySinceLastSubmit,
  hasSubmitErrors,
}: IProps) => {
  const {
    isDeletePortfolioFetching,
    isManagePortfoliosFetching,
  } = useManagePortfoliosContext();
  const { isLoading } = usePortfoliosContext();
  const { values } = useFormState();
  const [idOfVisibleDeleteModal, setIdOfVisibleDeleteModal] = useState(null);
  const saveButtonStyles = cn(styles.saveButton, {
    [styles.saveButtonFetching]: isManagePortfoliosFetching,
  });

  const updateFieldValues = () => {
    setFieldsData(
      (currentFieldsData: IFieldData[]) => currentFieldsData
        .map((fieldData: IFieldData) => {
          const fieldNames = Object.keys(values);
          const foundFieldName = fieldNames.find((fieldName) => fieldName === fieldData.id);
          const fieldDataAfterLastCharacterOfFieldErased = {
            ...fieldData,
            value: '',
          };
          return foundFieldName ?
            {
              ...fieldData,
              value: values[foundFieldName],
            } : fieldDataAfterLastCharacterOfFieldErased;
        }),
    );
  };

  useEffect(() => {
    updateFieldValues();
  }, [values]);

  const swapFields = (fieldIsOver, fieldIsUnder) => {
    setFieldsData((prevFieldsData) => {
      const oldFieldIndex = prevFieldsData
        .findIndex((fieldData: IFieldData) => fieldData.id === fieldIsOver.id);
      const newFieldIndex = prevFieldsData
        .findIndex((fieldData: IFieldData) => fieldData.id === fieldIsUnder.id);
      return arrayMove(prevFieldsData, oldFieldIndex, newFieldIndex);
    });
  };

  const onDragEnd = useCallback((event) => {
    const {
      active: fieldIsOver,
      over: fieldIsUnder,
    } = event;
    if (fieldIsOver.id === fieldIsUnder.id) {
      return;
    }
    swapFields(fieldIsOver, fieldIsUnder);
  }, []);

  const addPortfolio = () => {
    if (fieldsData.length === MAX_NUMBER_OF_PORTFOLIOS) {
      return;
    }
    setFieldsData((prevFieldsData) => ([
      ...prevFieldsData,
      {
        id: uuid(),
        portfolio: {
          name: '',
          id: null,
          priority: 0,
        },
        value: '',
      },
    ]));
  };

  const AddButton = () => {
    const addButtonStyles = cn(
      styles.addPortfolioButton,
      { [styles.addPortfolioButtonDisabled]: fieldsData.length === MAX_NUMBER_OF_PORTFOLIOS },
    );

    return (
      <div
        className={ addButtonStyles }
        onClick={ addPortfolio }
      >
        <IconAdd className={ styles.addIcon } />
        <div className={ styles.addPortfolioButtonTitle }>Add Portfolio</div>
      </div>
    );
  };

  const onClick = () => {
    setIdOfVisibleDeleteModal(null);
  };

  return (
    <div className={ styles.wrapper } onClick={ onClick }>
      <div className={ styles.header }>
        <div className={ styles.title }>Manage Portfolios</div>
        <div className={ styles.description }>
          You can add, rename, delete or reorder your Portfolios, that will be reflected on the page.
        </div>
      </div>
      <div className={ styles.scrollableContent }>
        <div className={ styles.list }>
          <div className={ styles.listHeader }>
            <div className={ styles.numberSign }>#</div>
            <div className={ styles.listHeaderTitle }>PORTFOLIO NAME</div>
          </div>
          <DndContext collisionDetection={ closestCenter } onDragEnd={ onDragEnd }>
            <SortableContext items={ fieldsData } strategy={ verticalListSortingStrategy }>
              {
                fieldsData.map((fieldData, index) => (
                  <SortablePortfolio
                    setFieldsData={ setFieldsData }
                    idOfVisibleDeleteModal={ idOfVisibleDeleteModal }
                    setIdOfVisibleDeleteModal={ setIdOfVisibleDeleteModal }
                    key={ fieldData.id }
                    fieldData={ fieldData }
                    index={ index }
                  />
                ))
              }
            </SortableContext>
          </DndContext>
        </div>
      </div>
      <div className={ styles.footer }>
        <Tooltip
          content={ tooltipContent }
          wrpClassName={ styles.addPortfolioButtonWrapper }
          disabled={ fieldsData.length < MAX_NUMBER_OF_PORTFOLIOS }
        >
          { AddButton() }
        </Tooltip>
        <div className={ styles.footerBottomButtons }>
          <Button
            className={ saveButtonStyles }
            variant={ ButtonVariantType.action }
            title="Save"
            dataTest="savePortfoliosButton"
            type="submit"
            disabled={
              isManagePortfoliosFetching ||
              isDeletePortfolioFetching ||
              isLoading ||
              (hasSubmitErrors && !dirtySinceLastSubmit)
            }
          >
            { (isManagePortfoliosFetching || isLoading)
              ? <Spinner className={ namePortfolioFormStyles.spinner } />
              : 'Save' }
          </Button>
          <Button
            onClick={ onCancel }
            variant={ ButtonVariantType.text }
            title="Cancel"
            dataTest="cancelButton"
          />
        </div>
      </div>
    </div>
  );
};

export default ManagePortfoliosForm;
